import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Chip, Skeleton, MenuItem, Select,
  FormControl, Button, Card, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import HeadsetMicRoundedIcon  from "@mui/icons-material/HeadsetMicRounded";
import BarChartRoundedIcon    from "@mui/icons-material/BarChartRounded";
import OpenInNewIcon          from "@mui/icons-material/OpenInNew";
import FilterAltRoundedIcon   from "@mui/icons-material/FilterAltRounded";
import RestartAltRoundedIcon  from "@mui/icons-material/RestartAltRounded";
import KeyboardArrowLeft      from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight     from "@mui/icons-material/KeyboardArrowRight";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  violet:  "#7C3AED", violetLt: "#EDE9FE",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  submitted:          { bg: T.amberLt,   color: T.amber,   label: "Submitted"          },
  under_review:       { bg: T.indigoLt,  color: T.indigo,  label: "Under Review"       },
  awaiting_response:  { bg: T.tealLt,    color: T.teal,    label: "Awaiting Response"  },
  resolved:           { bg: T.emeraldLt, color: T.emerald, label: "Resolved"           },
  closed:             { bg: "#F1F5F9",   color: T.muted,   label: "Closed"             },
  reopened:           { bg: T.roseLt,    color: T.rose,    label: "Reopened"           },
  escalated:          { bg: T.roseLt,    color: T.rose,    label: "Escalated"          },
};

// ── Priority config ───────────────────────────────────────────────────────
const PRIORITY_CFG = {
  low:      { color: T.muted,   bg: "#F1F5F9"  },
  medium:   { color: T.amber,   bg: T.amberLt  },
  high:     { color: T.rose,    bg: T.roseLt   },
  critical: { color: T.violet,  bg: T.violetLt },
};

// ── Category labels ───────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  damaged_goods:    "Damaged Goods",
  late_delivery:    "Late Delivery",
  delayed_delivery: "Delayed Delivery",
  driver_behaviour: "Driver Behaviour",
  billing_dispute:  "Billing Dispute",
  payment_dispute:  "Payment Dispute",
  lost_package:     "Lost Package",
  missing_goods:    "Missing Goods",
  other:            "Other",
};

const catLabel = (c) => CATEGORY_LABELS[c] || c?.replace(/_/g, " ")?.replace(/\b\w/g, x => x.toUpperCase()) || "—";
const fmtDate  = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

// ── Status chip ───────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CFG[status] || { bg: "#F1F5F9", color: T.muted, label: status };
  return (
    <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:cfg.bg, border:`1px solid ${cfg.color}22` }}>
      <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:cfg.color }} />
      <Typography sx={{ fontSize:11, fontWeight:700, color:cfg.color, fontFamily:"'Plus Jakarta Sans',sans-serif", textTransform:"capitalize" }}>
        {cfg.label}
      </Typography>
    </Box>
  );
};

// ── SLA chip ──────────────────────────────────────────────────────────────
const SlaChip = ({ breached }) => breached ? (
  <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:T.roseLt, border:`1px solid ${T.rose}22` }}>
    <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:T.rose }} />
    <Typography sx={{ fontSize:11, fontWeight:700, color:T.rose }}>SLA Breached</Typography>
  </Box>
) : (
  <Typography sx={{ fontSize:12, color:T.emerald, fontWeight:500 }}>On Track</Typography>
);

// ── Filter pill ───────────────────────────────────────────────────────────
const FPill = ({ label, active, color, bg, onClick }) => (
  <Box onClick={onClick} sx={{
    px:1.6, py:0.6, borderRadius:"8px", cursor:"pointer",
    bgcolor: active ? bg : "transparent", color: active ? color : T.muted,
    fontWeight: active ? 700 : 500, fontSize:12.5,
    border:`1.5px solid ${active ? color+"44" : T.border}`,
    transition:"all 0.14s", whiteSpace:"nowrap",
    "&:hover":{ bgcolor:bg, color }, fontFamily:"'Plus Jakarta Sans',sans-serif",
  }}>{label}</Box>
);

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkelRow = () => (
  <TableRow>
    {[90,130,100,80,120,100,120,60].map((w,i) => <TableCell key={i}><Skeleton width={w} height={16} /></TableCell>)}
  </TableRow>
);

// ── Main component ────────────────────────────────────────────────────────
export default function Complaints() {
  const navigate = useNavigate();

  const [complaints,       setComplaints]       = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [page,             setPage]             = useState(1);
  const [totalPages,       setTotalPages]       = useState(1);
  const [filterStatus,     setFilterStatus]     = useState("");
  const [filterCategory,   setFilterCategory]   = useState("");
  const [filterSla,        setFilterSla]        = useState("");
  const [filterAssignedMe, setFilterAssignedMe] = useState(false);

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterStatus)     params.status       = filterStatus;
      if (filterCategory)   params.category     = filterCategory;
      if (filterSla)        params.sla_breached = filterSla;
      if (filterAssignedMe) params.assigned_to_me = true;
      const res = await getData(API_ENDPOINTS.getAllComplaints, params);
      if (res?.result) { setComplaints(res.result.complaints || []); setTotalPages(res.result.totalPages || 1); }
    } catch (_) {} finally { setLoading(false); }
  }, [page, filterStatus, filterCategory, filterSla, filterAssignedMe]);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  const handleReset = () => { setFilterStatus(""); setFilterCategory(""); setFilterSla(""); setFilterAssignedMe(false); setPage(1); };

  const isSlaBreached = c => c.triage_sla_breached || c.resolution_sla_breached;

  // Summary counts
  const escalatedCount = complaints.filter(c => c.status === "escalated").length;
  const slaCount       = complaints.filter(isSlaBreached).length;
  const openCount      = complaints.filter(c => !["closed","resolved"].includes(c.status)).length;

  return (
    <Box sx={{ bgcolor: T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{
        background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)",
      }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <HeadsetMicRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Complaints & Disputes</Typography>
            <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.3 }}>Track and resolve customer complaints and delivery disputes</Typography>
          </Box>
        </Box>

        <Box sx={{ display:"flex", gap:2, alignItems:"center" }}>
          {/* Summary pills */}
          {!loading && (
            <Box sx={{ display:"flex", gap:1.5 }}>
              {[
                { label:"Total",     value:complaints.length, bg:"rgba(255,255,255,0.15)", color:"#fff"      },
                { label:"Open",      value:openCount,          bg:T.amberLt,               color:T.amber     },
                { label:"Escalated", value:escalatedCount,     bg:T.roseLt,                color:T.rose      },
                { label:"SLA Breach",value:slaCount,           bg:T.roseLt,                color:T.rose      },
              ].map(s => (
                <Box key={s.label} sx={{ display:"flex", alignItems:"center", gap:0.8, bgcolor:s.bg, borderRadius:"10px", px:1.5, py:0.7, border:s.color==="#fff"?"1px solid rgba(255,255,255,0.25)":`1px solid ${s.color}22` }}>
                  <Typography sx={{ fontSize:16, fontWeight:800, color:s.color, lineHeight:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.value}</Typography>
                  <Typography sx={{ fontSize:11, fontWeight:600, color:s.color, opacity:0.85 }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Button variant="outlined" size="small" startIcon={<BarChartRoundedIcon sx={{ fontSize:15 }} />}
            onClick={() => navigate("analytics")}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, color:"#fff", borderColor:"rgba(255,255,255,0.4)", bgcolor:"rgba(255,255,255,0.1)", "&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
            View Analytics
          </Button>
        </Box>
      </Box>

      {/* ── Filter toolbar ── */}
      <Box sx={{ display:"flex", gap:1.5, alignItems:"center", flexWrap:"wrap", mb:2.5 }}>
        {/* Status */}
        <FormControl size="small">
          <Select value={filterStatus} displayEmpty onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:150 }}>
            <MenuItem value=""><em style={{ color:T.muted }}>All Statuses</em></MenuItem>
            {Object.entries(STATUS_CFG).map(([k,v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize:13 }}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Category */}
        <FormControl size="small">
          <Select value={filterCategory} displayEmpty onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:170 }}>
            <MenuItem value=""><em style={{ color:T.muted }}>All Categories</em></MenuItem>
            {Object.entries(CATEGORY_LABELS).map(([k,v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize:13 }}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* SLA */}
        <FormControl size="small">
          <Select value={filterSla} displayEmpty onChange={e => { setFilterSla(e.target.value); setPage(1); }}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:160 }}>
            <MenuItem value=""><em style={{ color:T.muted }}>All SLA</em></MenuItem>
            <MenuItem value="triage"     sx={{ fontSize:13 }}>Triage Breached</MenuItem>
            <MenuItem value="resolution" sx={{ fontSize:13 }}>Resolution Breached</MenuItem>
          </Select>
        </FormControl>

        {/* Assigned to me pill */}
        <FPill label="Assigned to Me" active={filterAssignedMe} color={T.teal} bg={T.tealLt}
          onClick={() => { setFilterAssignedMe(v => !v); setPage(1); }} />

        {/* Reset */}
        <Tooltip title="Reset filters">
          <IconButton size="small" onClick={handleReset} sx={{ bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"10px" }}>
            <RestartAltRoundedIcon sx={{ fontSize:18, color:T.muted }} />
          </IconButton>
        </Tooltip>

        {!loading && (
          <Typography sx={{ fontSize:13, color:T.muted, ml:"auto" }}>
            {complaints.length} complaint{complaints.length !== 1 ? "s" : ""}
          </Typography>
        )}
      </Box>

      {/* ── Table ── */}
      <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:T.bg }}>
                {["Ref","Category","Status","Priority","Package / Trip","SLA","Filed At",""].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", py:1.5, borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_,i) => <SkelRow key={i} />)
                : complaints.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py:6 }}>
                      <HeadsetMicRoundedIcon sx={{ fontSize:40, color:T.border, mb:1, display:"block", mx:"auto" }} />
                      <Typography fontSize={13} color="text.secondary">No complaints found.</Typography>
                    </TableCell>
                  </TableRow>
                )
                : complaints.map(c => {
                  const priCfg = PRIORITY_CFG[c.priority] || PRIORITY_CFG.low;
                  return (
                    <TableRow key={c.complaint_id} hover
                      sx={{ cursor:"pointer", "&:last-child td":{ border:0 }, "&:hover":{ bgcolor:T.tealLt+"33" } }}
                      onClick={() => navigate(`${c.complaint_id}`)}>

                      {/* Ref */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, fontWeight:700, color:T.teal, fontFamily:"monospace" }}>{c.complaint_ref}</Typography>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, fontWeight:500, color:T.text }}>{catLabel(c.category)}</Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell><StatusChip status={c.status} /></TableCell>

                      {/* Priority */}
                      <TableCell>
                        {c.priority ? (
                          <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1, py:0.3, bgcolor:priCfg.bg, borderRadius:"7px" }}>
                            <Typography sx={{ fontSize:11, fontWeight:700, color:priCfg.color, textTransform:"capitalize" }}>{c.priority}</Typography>
                          </Box>
                        ) : <Typography sx={{ fontSize:12, color:T.muted }}>—</Typography>}
                      </TableCell>

                      {/* Package / Trip */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, color:T.muted }}>
                          {c.package_id ? `Pkg #${c.package_id}` : "—"}
                          {" / "}
                          {c.trip_id    ? `Trip #${c.trip_id}`    : "—"}
                        </Typography>
                      </TableCell>

                      {/* SLA */}
                      <TableCell><SlaChip breached={isSlaBreached(c)} /></TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, color:T.muted }}>{fmtDate(c.createdAt)}</Typography>
                      </TableCell>

                      {/* Action */}
                      <TableCell padding="none">
                        <Tooltip title="View complaint">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`${c.complaint_id}`); }}
                            sx={{ color:T.muted }}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && complaints.length > 0 && (
          <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", px:2.5, py:1.5, borderTop:`1px solid ${T.border}` }}>
            <Typography sx={{ fontSize:13, color:T.muted }}>Page {page} of {totalPages}</Typography>
            <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
              <IconButton size="small" onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                sx={{ bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"8px" }}>
                <KeyboardArrowLeft sx={{ fontSize:18 }} />
              </IconButton>
              <Typography sx={{ fontSize:13, fontWeight:600, color:T.text, minWidth:56, textAlign:"center" }}>
                {page} / {totalPages}
              </Typography>
              <IconButton size="small" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
                sx={{ bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"8px" }}>
                <KeyboardArrowRight sx={{ fontSize:18 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
}
